import PropTypes from "prop-types";
import { useState } from "react";
import { Link } from "react-router-dom";

import FeatureFaster from "../../assets/images/newhome/feature-faster.png";
import FeatureFit from "../../assets/images/newhome/feature-fit.png";
import FeatureLess from "../../assets/images/newhome/feature-less.png";
import HeroImg from "../../assets/images/newhome/hero.png";
import Logo from "../../assets/images/un_resume_logo2.png";

/*
 * NewHomePage — a faithful reproduction of the current un-resume.tech marketing
 * homepage (built in Framer) rebuilt in the app's React + Tailwind stack so it
 * can eventually replace the "/" route.
 *
 * Design tokens taken from the live Framer site:
 *   - Primary blue   #1D60E5
 *   - Ink / dark bg   #101011
 *   - Hero gradient   white -> rgba(29,96,229,0.3) band -> white
 *   - Headings        Inter Display (Inter here), 600/700
 * Illustration/icon assets were pulled from the Framer site and are bundled
 * locally (the app's CSP only allows images from 'self').
 */

const PRIMARY = "#1D60E5";
const INK = "#101011";
const FONT = "'Inter', 'Inter Display', system-ui, -apple-system, sans-serif";

const HERO_IMG = HeroImg;

const FEATURES = [
  {
    icon: FeatureFit,
    title: "Better Fit, Not Better Formatting",
    body: "Unresume matches people to opportunities based on real fit, not keywords. Skills, interests, availability, and intent matter more than titles or formatting.",
  },
  {
    icon: FeatureLess,
    title: "Less Applying. Less Screening.",
    body: "Candidates create one profile and connect only with roles that actually fit. Recruiters see only people who are qualified and genuinely interested.",
  },
  {
    icon: FeatureFaster,
    title: "Faster, Clearer Matches",
    body: "No mass applications. No resume black holes. No wasted time. Just better matches, better conversations, and better outcomes — for everyone.",
  },
];

const STEPS = [
  {
    title: "Find Opportunites",
    body: "No mass applying just tell us what you want once!",
  },
  {
    title: "Get Right Talent",
    body: "Precision matches based on fit and AI fluency",
  },
  {
    title: "Define What Mattres",
    body: "Tell us what you’re really looking for",
  },
  {
    title: "Review Curated Matches",
    body: "Quality over quantity, just relevant ready to engage matches",
  },
  {
    title: "Connect with Confidence",
    body: "Connections feel natural, not transactional.",
  },
];

// The live FAQ answers load only when a row is expanded and aren't present in
// the page source, so these are brand-consistent placeholders to be replaced
// with the real copy.
const FAQS = [
  {
    q: "How does UnResume work for job seekers?",
    a: "You build one profile that captures your skills, interests, availability, and what you actually want. UnResume then surfaces only the roles that genuinely fit — no mass applying, no resume black holes.",
  },
  {
    q: "How does it help recruiters?",
    a: "Recruiters see only candidates who are qualified and genuinely interested, ranked by real fit rather than keyword-stuffed resumes. Less screening, better conversations.",
  },
  {
    q: "What makes UnResume different?",
    a: "We match on real alignment — skills, intent, and availability — instead of formatting and titles. The result is fewer, higher-quality matches for everyone.",
  },
  {
    q: "Is the platform secure?",
    a: "Your data is handled with care and never shared without your consent. You stay in control of your profile and who can connect with you.",
  },
];

function CtaButton({ to, children, variant = "primary", className = "" }) {
  const base =
    "inline-flex items-center gap-2 rounded-lg px-6 py-3 text-base font-semibold transition-transform hover:-translate-y-0.5";
  const styles =
    variant === "primary"
      ? { backgroundColor: PRIMARY, color: "#fff" }
      : { backgroundColor: "#fff", color: INK };
  return (
    <Link className={`${base} ${className}`} style={styles} to={to}>
      {children}
      <span
        className="flex h-6 w-6 items-center justify-center rounded-full"
        style={{
          backgroundColor:
            variant === "primary" ? "rgba(255,255,255,0.2)" : PRIMARY,
          color: "#fff",
        }}
      >
        →
      </span>
    </Link>
  );
}

CtaButton.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(["primary", "light"]),
  className: PropTypes.string,
};

function FaqRow({ item, isOpen, onToggle }) {
  return (
    <div className="border-b border-gray-200">
      <button
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between py-5 text-left"
        type="button"
        onClick={onToggle}
      >
        <span className="text-lg font-semibold" style={{ color: INK }}>
          {item.q}
        </span>
        <span
          className="ml-4 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xl"
          style={{
            backgroundColor: isOpen ? PRIMARY : "#F1F2F6",
            color: isOpen ? "#fff" : INK,
          }}
        >
          {isOpen ? "−" : "+"}
        </span>
      </button>
      {isOpen && (
        <p className="pb-6 pr-12 text-base leading-relaxed text-gray-600">
          {item.a}
        </p>
      )}
    </div>
  );
}

FaqRow.propTypes = {
  item: PropTypes.shape({ q: PropTypes.string, a: PropTypes.string })
    .isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default function NewHomePage() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div
      className="min-h-screen bg-white"
      style={{ fontFamily: FONT, color: INK }}
    >
      {/* Scoped webfont import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
      `}</style>

      {/* ---------------- Nav ---------------- */}
      <header className="sticky top-0 z-50 border-b border-gray-100 bg-white/80 backdrop-blur">
        <nav className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link className="flex items-center gap-2" to="/">
            <img alt="UnResumé" className="h-9 w-9" src={Logo} />
            <span className="text-xl font-bold" style={{ color: INK }}>
              UnResumé
            </span>
          </Link>
          <a
            className="rounded-lg px-5 py-2.5 text-sm font-semibold text-white"
            href="mailto:admin@un-resume.tech"
            style={{ backgroundColor: PRIMARY }}
          >
            Contact Us
          </a>
        </nav>
      </header>

      {/* ---------------- Hero ---------------- */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, #ffffff 0%, rgba(29,96,229,0.28) 32%, rgba(29,96,229,0.28) 70%, #ffffff 100%)",
        }}
      >
        <div className="mx-auto max-w-5xl px-6 pt-24 pb-16 text-center">
          <h1
            className="mx-auto max-w-4xl text-4xl font-semibold leading-[1.08] tracking-tight sm:text-6xl md:text-[70px]"
            style={{ color: INK }}
          >
            Redefining hiring that goes beyond the résumé.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-gray-600 sm:text-xl">
            Unresume matches talent with opportunities through real alignment
          </p>
          <div className="mt-9 flex justify-center">
            <CtaButton to="/signup">Get Your Match</CtaButton>
          </div>
        </div>
        <div className="mx-auto max-w-6xl px-6 pb-4">
          <img
            alt="How UnResume matches talent to opportunities"
            className="mx-auto w-full max-w-4xl"
            src={HERO_IMG}
          />
        </div>
      </section>

      {/* ---------------- Features ---------------- */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p
              className="text-sm font-semibold uppercase tracking-wide"
              style={{ color: PRIMARY }}
            >
              Built for better matches
            </p>
            <h2
              className="mt-3 text-4xl font-bold tracking-tight sm:text-5xl"
              style={{ color: INK }}
            >
              Better Fit, Not Better Formatting
            </h2>
            <p className="mt-5 text-lg text-gray-600">
              Unresume matches people to opportunities based on real fit, not
              keywords. Skills, interests, availability, and intent matter more
              than titles or formatting.
            </p>
          </div>
          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm transition-shadow hover:shadow-md"
              >
                <img alt="" className="h-16 w-16 object-contain" src={f.icon} />
                <h3
                  className="mt-6 text-xl font-semibold"
                  style={{ color: INK }}
                >
                  {f.title}
                </h3>
                <p className="mt-3 text-base leading-relaxed text-gray-600">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- How it works (dark) ---------------- */}
      <section className="py-24" style={{ backgroundColor: INK }}>
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-center text-4xl font-bold tracking-tight text-white sm:text-5xl">
            How it works
          </h2>
          <div className="mt-16 flex flex-col items-stretch gap-6 lg:flex-row lg:items-center">
            {STEPS.map((s, i) => (
              <div
                key={s.title}
                className="flex flex-1 items-center gap-6 lg:flex-col lg:gap-0"
              >
                <div className="flex-1 rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center">
                  <div
                    className="mx-auto flex h-12 w-12 items-center justify-center rounded-full text-lg font-bold text-white"
                    style={{ backgroundColor: PRIMARY }}
                  >
                    {i + 1}
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-white">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-400">
                    {s.body}
                  </p>
                </div>
                {i < STEPS.length - 1 && (
                  <span className="hidden text-2xl text-white/40 lg:block">
                    →
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- FAQ ---------------- */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center">
            <p
              className="text-sm font-semibold uppercase tracking-wide"
              style={{ color: PRIMARY }}
            >
              FAQ’s
            </p>
            <h2
              className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl"
              style={{ color: INK }}
            >
              Frequently Asked Questions
            </h2>
          </div>
          <div className="mt-12">
            {FAQS.map((item, i) => (
              <FaqRow
                key={item.q}
                isOpen={openFaq === i}
                item={item}
                onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- CTA banner ---------------- */}
      <section className="bg-white px-6 pb-24">
        <div
          className="mx-auto max-w-6xl overflow-hidden rounded-3xl px-8 py-16 text-center"
          style={{ backgroundColor: INK }}
        >
          <h2 className="mx-auto max-w-2xl text-3xl font-bold text-white sm:text-4xl">
            Let AI Power Your Hiring and Job Search
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-gray-400">
            UnResume connects top talent with the right opportunities using
            smart, data-driven algorithms. Experience faster, better matches
            without the hassle.
          </p>
          <div className="mt-9 flex justify-center">
            <CtaButton to="/signup" variant="light">
              Get Started
            </CtaButton>
          </div>
        </div>
      </section>

      {/* ---------------- Footer ---------------- */}
      <footer className="border-t border-gray-100 bg-white py-12">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-6 text-center md:flex-row md:justify-between md:text-left">
          <div className="flex items-center gap-2">
            <img alt="UnResumé" className="h-8 w-8" src={Logo} />
            <span className="text-lg font-bold" style={{ color: INK }}>
              UnResumé
            </span>
          </div>
          <p className="text-sm text-gray-500">
            Smarter Matches, Faster Results
          </p>
          <div className="text-sm text-gray-500">
            <a className="hover:underline" href="mailto:admin@un-resume.tech">
              admin@un-resume.tech
            </a>
          </div>
        </div>
        <p className="mx-auto mt-6 max-w-6xl px-6 text-center text-xs text-gray-400">
          Please review our{" "}
          <a className="underline" href="/policy">
            Terms of Service and Privacy Policy
          </a>{" "}
          for details.
        </p>
      </footer>
    </div>
  );
}
